import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef(({ className, variant = "default", ...props }, ref) => {
  // Generate unique ID for each card instance to avoid SVG pattern conflicts
  const patternId = React.useMemo(() => `pattern-${Math.random().toString(36).substr(2, 9)}`, []);

  const getPatternSVG = (patternType) => {
    switch (patternType) {
      case 'default':
        return (
          <svg className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden="true" style={{ opacity: 0.06 }}>
            <defs>
              <pattern id={`grid-${patternId}`} width="16" height="16" patternUnits="userSpaceOnUse">
                <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#64748b" strokeWidth="0.4"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#grid-${patternId})`} />
          </svg>
        );
      case 'lines':
        return (
          <svg className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden="true" style={{ opacity: 0.07 }}>
            <defs>
              <pattern id={`lines-${patternId}`} width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 0 16 L 32 16" fill="none" stroke="#64748b" strokeWidth="0.5"/>
                <path d="M 16 0 L 16 32" fill="none" stroke="#64748b" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#lines-${patternId})`} />
          </svg>
        );
      case 'dots':
        return (
          <svg className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden="true" style={{ opacity: 0.06 }}>
            <defs>
              <pattern id={`dots-${patternId}`} width="12" height="12" patternUnits="userSpaceOnUse">
                <circle cx="6" cy="6" r="0.5" fill="#64748b"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#dots-${patternId})`} />
          </svg>
        );
      case 'diagonal':
        return (
          <svg className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden="true" style={{ opacity: 0.06 }}>
            <defs>
              <pattern id={`diagonal-${patternId}`} width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 0 20 L 20 0" fill="none" stroke="#64748b" strokeWidth="0.4"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#diagonal-${patternId})`} />
          </svg>
        );
      default:
        return null;
    }
  };

  // Use consistent pattern for 'pattern' variant (dots for uniformity)
  const selectedPattern = variant === 'pattern' 
    ? 'dots'
    : variant && ['default', 'lines', 'dots', 'diagonal', 'none'].includes(variant)
      ? variant 
      : 'default';

  return (
    <div
      ref={ref}
      className={cn(
        "relative rounded-xl border bg-card text-card-foreground transition-all duration-300",
        "hover:border-slate-300/60 hover:-translate-y-0.5",
        className
      )}
      {...props}
    >
      {getPatternSVG(selectedPattern)}
      {props.children}
    </div>
  );
})
Card.displayName = "Card"

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props} />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props} />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props} />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
